import { useLocation } from "@remix-run/react";
import { IconDotsVertical, IconSearch } from "@tabler/icons-react";
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { useIsMobile } from "#app/hooks/use-mobile";
import { cn } from "#app/utils/misc.tsx";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "../ui/input-group";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Separator } from "../ui/separator";
import { OrgTeamSelector } from "./org-team-selector";

type PageHeaderProps = {
  /** Simple text title. Use `left` instead when the slot needs tabs or a popover. */
  title?: ReactNode;
  left?: ReactNode;
  center?: ReactNode;
  right?: ReactNode;
  /** Renders the org/team selector flush against the right edge. */
  showOrganization?: boolean;
  /** Keeps a compact page-specific action visible beside the title on tablets. */
  keepRightOnTablet?: boolean;
  /** A compact action rendered directly in the header before its overflow menu. */
  compactAction?: ReactNode;
  className?: string;
};

const MobileHeaderMenuContext = createContext<{ close: () => void } | null>(
  null,
);

/** Close the mobile header ⋮ panel from actions inside `right` (search, buttons). */
export function useMobileHeaderMenu() {
  return useContext(MobileHeaderMenuContext);
}

type HeaderSearchButtonProps = {
  value: string;
  onChange: (value: string) => void;
  title: string;
  description?: string;
  className?: string;
  onSubmit?: () => void;
};

/** Search icon + focused dialog used by mobile and tablet page headers. */
export function HeaderSearchButton({
  value,
  onChange,
  title,
  description,
  className,
  onSubmit,
}: HeaderSearchButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        className={cn("shrink-0 xl:hidden", className)}
        aria-label={title}
        title={title}
        onClick={() => setOpen(true)}
      >
        <IconSearch size={18} />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? (
              <DialogDescription>{description}</DialogDescription>
            ) : null}
          </DialogHeader>
          <InputGroup className="w-full">
            <InputGroupInput
              autoFocus
              placeholder={title}
              value={value}
              onChange={(event) => onChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                onSubmit?.();
                setOpen(false);
              }}
            />
            <InputGroupAddon align="inline-start">
              <IconSearch className="text-primary" size={18} />
            </InputGroupAddon>
          </InputGroup>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Sticky page chrome shared by every screen in the new design: translucent while
 * the page is at the top, opaque with a hairline shadow once it scrolls.
 */
export function PageHeader({
  title,
  left,
  center,
  right,
  showOrganization = false,
  keepRightOnTablet = false,
  compactAction,
  className,
}: PageHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const isCompactHeader = useIsMobile(1280);
  const location = useLocation();
  const showMobileMenu =
    isCompactHeader &&
    (isMobile || !keepRightOnTablet) &&
    Boolean(right || (isMobile && showOrganization));

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <MobileHeaderMenuContext.Provider
      value={{ close: () => setMobileMenuOpen(false) }}
    >
      <header
        className={cn(
          "sticky top-0 z-20 border-b transition-all duration-300",
          scrolled
            ? "border-zinc-200 bg-white shadow-xs dark:border-border dark:bg-zinc-900"
            : "border-zinc-200/60 bg-white/60 backdrop-blur-xl dark:border-border/60 dark:bg-zinc-900/40",
          className,
        )}
      >
        {/* The visible tablet/desktop org selector sits flush against the content
            column's right edge. On mobile it moves into the overflow panel, so
            retain an inset for the exposed search and overflow buttons. */}
        <div
          className={cn(
            "flex items-center justify-between gap-4 pl-6",
            showOrganization ? "pr-4 md:pr-0" : "pr-6",
          )}
        >
          <div className="flex min-h-14 min-w-0 items-center gap-4 pl-7 md:pl-0">
            {title ? (
              <h1 className="truncate py-4 font-medium">{title}</h1>
            ) : null}
            {left}
          </div>

          {center ? (
            <div className="flex items-center justify-center gap-3">
              {center}
            </div>
          ) : null}

          <div className="flex shrink-0 items-center gap-2">
            {compactAction}
            {showMobileMenu ? (
              // Popover (not DropdownMenu): the mobile panel often holds search
              // inputs. Menu primitives capture keyboard/typeahead and block typing.
              <Popover
                modal={false}
                open={mobileMenuOpen}
                onOpenChange={setMobileMenuOpen}
              >
                <PopoverTrigger
                  render={
                    <Button
                      variant="secondary"
                      size="icon"
                      className={cn(
                        "fixed right-4 top-2.5 z-30 md:max-xl:static",
                        compactAction && "static",
                      )}
                      aria-label="More options"
                    >
                      <IconDotsVertical size={20} strokeWidth={2.5} />
                    </Button>
                  }
                />

                <PopoverContent
                  align="end"
                  side="bottom"
                  sideOffset={8}
                  className="z-50 w-[min(22rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] gap-0 overflow-visible rounded-3xl p-0 pb-1 shadow-lg"
                  initialFocus={false}
                >
                  <div className="flex w-full min-w-0 flex-col">
                    {right ? (
                      // Full-width search/selects in the narrow ⋮ panel; leave
                      // button rows to each page's HeaderTools (don't force
                      // every child into a column — that split Import/Export
                      // away from the create button on Operators).
                      <div className="flex w-full min-w-0 flex-col gap-2 p-2 **:data-[slot=input-group]:w-full **:data-[slot=input-group]:min-w-0 **:data-[slot=select-trigger]:w-full">
                        {right}
                      </div>
                    ) : null}
                    {showOrganization && isMobile ? (
                      <>
                        <Separator className="bg-zinc-100 dark:bg-zinc-800" />
                        <OrgTeamSelector
                          inline
                          onSelectionChange={() => setMobileMenuOpen(false)}
                        />
                      </>
                    ) : null}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <div className="flex items-center gap-3">
                {right}
                {showOrganization ? <OrgTeamSelector /> : null}
              </div>
            )}
            {showMobileMenu && showOrganization && !isMobile ? (
              <OrgTeamSelector />
            ) : null}
          </div>
        </div>
      </header>
    </MobileHeaderMenuContext.Provider>
  );
}

export default PageHeader;
